// @spec SHL-STR-022

import { useCallback, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

export interface UseKeyboardShortcutsOptions {
  /** Whether shortcuts help dialog is initially open */
  initialOpen?: boolean;
}

export interface UseKeyboardShortcutsReturn {
  /** Whether the shortcuts dialog is open */
  isOpen: boolean;
  /** Open the shortcuts dialog */
  openShortcuts: () => void;
  /** Close the shortcuts dialog */
  closeShortcuts: () => void;
  /** Toggle the shortcuts dialog */
  toggleShortcuts: () => void;
}

/**
 * Hook for managing the keyboard shortcuts help dialog.
 *
 * Registers the `?` / `Shift+/` shortcut to toggle the dialog
 * and provides state and controls.
 *
 * @example
 * function ShortcutsProvider({ children }) {
 *   const { isOpen, closeShortcuts } = useKeyboardShortcuts();
 *
 *   return (
 *     <>
 *       {children}
 *       <KeyboardShortcutsDialog
 *         open={isOpen}
 *         onOpenChange={(open) => !open && closeShortcuts()}
 *       />
 *     </>
 *   );
 * }
 */
export function useKeyboardShortcuts(
  options: UseKeyboardShortcutsOptions = {}
): UseKeyboardShortcutsReturn {
  const { initialOpen = false } = options;
  const [isOpen, setIsOpen] = useState(initialOpen);

  const openShortcuts = useCallback(() => {
    setIsOpen(true);
  }, []);
  const closeShortcuts = useCallback(() => {
    setIsOpen(false);
  }, []);
  const toggleShortcuts = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  // Register the keyboard shortcut for showing help
  // Using 'shift+/' which is '?' on US keyboards
  useHotkeys(
    'shift+/',
    (event) => {
      event.preventDefault();
      toggleShortcuts();
    },
    {
      scopes: ['global'],
      enableOnFormTags: false,
      enableOnContentEditable: false,
    }
  );

  // Also register '?' directly for international keyboard layouts
  useHotkeys(
    '?',
    (event) => {
      event.preventDefault();
      toggleShortcuts();
    },
    {
      scopes: ['global'],
      enableOnFormTags: false,
      enableOnContentEditable: false,
    }
  );

  return {
    isOpen,
    openShortcuts,
    closeShortcuts,
    toggleShortcuts,
  };
}
